import React, { useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { Dispatch } from 'redux';
import Icon from 'flow/components/ui-components/Icon';
import MenuItem from 'flow/components/ui-components/MenuItem';
import NestedMenuItem from 'flow/components/ui-components/NestedMenuItem';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import {
    selectAssignee,
    selectTicketKeysMineSorted,
    selectTicketViewIsMounted,
    selectMembers,
    selectTicketsListTabSelected,
} from 'flow/selectors/TicketsSelectors';
import { selectOwnUserHasTicketPermission } from 'flow/selectors/ticket-permission-selectors';
import { updateTicketAssignee } from 'flow/actions/tickets-action';
import { TICKET_STRINGS } from 'flow/utils/segment-utils';
import { segmentTrack } from 'flow/actions/segment-actions';
import {
    selectExtensions,
    selectOwnExtensionKey,
} from 'flow/selectors/ExtensionSelectors';
import SelectAssigneeAvatar from 'flow/components/tickets/Assignee/SelectAssigneeAvatar';
import { State } from 'flow/types/State';
import { ExtensionKey, TicketKey } from 'flow/types/EntityKeys';
import { history } from 'flow/configure-store';
import { generatePath } from 'flow/utils/router-utils';
import { routes } from 'flow/types/routes';
import {
    TICKETS_LIST_TABS_KEYS,
    getEntityKeyNextKey,
    getTicketAssigneeOptionsFromMembers,
} from 'flow/utils/ticket-utils';
import useTicketAction from 'flow/hooks/useTicketAction';

type Props = {
    ticketKey: TicketKey;
};

const handlePostAssignmentNavigation =
    (ticketKey: TicketKey, previousAssignee: ExtensionKey | 'unassigned') =>
    (dispatch: Dispatch<any>, getState: () => State) => {
        const state = getState();
        const ownExtensionKey = selectOwnExtensionKey(state);
        const tabSelected = selectTicketsListTabSelected(state);
        const ticketKeysMyTicketsTab = selectTicketKeysMineSorted(state);
        const ticketIsMounted = selectTicketViewIsMounted(state, ticketKey);

        if (
            tabSelected === TICKETS_LIST_TABS_KEYS.MY_TICKETS &&
            ownExtensionKey === previousAssignee &&
            ticketIsMounted
        ) {
            const nextKey = getEntityKeyNextKey(
                ticketKeysMyTicketsTab,
                ticketKey,
            );
            if (nextKey) {
                history.push(
                    generatePath(routes['tickets.ticketKey'], {
                        ticketKey: nextKey,
                    }),
                );
            } else {
                history.push(routes['tickets']);
            }
        }
    };

const handleAssignmentError = (error: any, openModal: any, i18next: any) => {
    if (error.status === 404) {
        openModal('confirmationDialog', {
            title: i18next.t('tickets.eligible.title'),
            description: i18next.t('tickets.eligible.ccpro'),
            handleAccept: () => {},
            hideCancelButton: true,
        });
    }
};

const performTicketAssignment =
    (
        ticketKey: TicketKey,
        assignee: ExtensionKey | 'unassigned',
        // The previous assignee decides where we go next, and only a thunk can
        // read it, so the runner is handed in: a hook cannot be called in here.
        runTicketAction: ReturnType<typeof useTicketAction>,
        openModal: any,
        i18next: any,
    ) =>
    (dispatch: Dispatch<any>, getState: () => State) => {
        const previousAssignee = selectAssignee(getState(), ticketKey);
        runTicketAction(updateTicketAssignee(ticketKey, assignee))
            .then(() => {
                dispatch(
                    handlePostAssignmentNavigation(ticketKey, previousAssignee),
                );
            })
            .catch((error: any) => {
                handleAssignmentError(error, openModal, i18next);
            });
    };

const MenuItemAssignMenu = ({ ticketKey }: Props) => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const runTicketAction = useTicketAction();
    const { openModal } = useModal();

    const selectedAssignee = useAppSelector((state) =>
        selectAssignee(state, ticketKey),
    );
    const canAssign = useAppSelector((state) =>
        selectOwnUserHasTicketPermission(state, ticketKey, 'assign'),
    );
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);
    const extensions = useAppSelector(selectExtensions);
    const members = useAppSelector((state) => selectMembers(state, ticketKey));

    const assigneeOptions = useMemo(
        () =>
            getTicketAssigneeOptionsFromMembers(
                i18next,
                ownExtensionKey,
                members,
                extensions,
            ),
        [i18next, ownExtensionKey, members, extensions],
    );

    const handleSelectedAssignee = (
        event: React.MouseEvent,
        assignee: ExtensionKey | 'unassigned',
    ) => {
        event?.stopPropagation();
        if (!canAssign) {
            return;
        }

        dispatch(
            segmentTrack(TICKET_STRINGS.USER_ASSIGNED_TICKET, {
                component: 'ticket context menu',
            }),
        );

        dispatch(
            performTicketAssignment(
                ticketKey,
                assignee,
                runTicketAction,
                openModal,
                i18next,
            ),
        );
    };

    return (
        <NestedMenuItem title={i18next.t('ticket.assign.to')}>
            {Object.values(assigneeOptions).map((option) => (
                <MenuItem
                    key={option.key}
                    disabled={!canAssign}
                    tooltipLabel={
                        canAssign
                            ? undefined
                            : i18next.t('tickets.no_permission')
                    }
                    onClick={(event) =>
                        handleSelectedAssignee(event, option.key)
                    }
                    className="w-56"
                >
                    <div className="me-3 flex">
                        <SelectAssigneeAvatar assigneeKey={option.key} />
                    </div>
                    {option.label}
                    {selectedAssignee === option.key ? (
                        <div className="ms-auto">
                            <Icon iconKey="done" size={'1rem'} />
                        </div>
                    ) : null}
                </MenuItem>
            ))}
        </NestedMenuItem>
    );
};

export default MenuItemAssignMenu;
